import React, { useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, Grid, GridCell, NotificationInline,
   Text, View } from 'oio-react'
import { parse as parseQueryString } from 'query-string'
import Helmet from 'react-helmet'
import { Link, useLocation } from 'react-router-dom'
import AccountContainer from 'src/components/Account/Container'
import { Input } from 'src/components/ConnectedForm'
import { arrayToSentenceFragment } from 'src/components/utils'
import { useOrganization } from 'src/graphql/hooks'
import apiFetch from 'src/libraries/services/apiFetch'

// =======================================================
// Login Component
// =======================================================

const Login = () => {
   const location = useLocation()
   const { redirect } = parseQueryString(location.search)

   const { organization } = useOrganization()
   const [error, setError] = useState(null)

   const highlightColor = organization.theme.highlightColor || '#000'
   const enabledInitiativeTypeNames = organization.initiativeTypes
      .filter(t => t.enabled)
      .map(t => t.namePlural)

   const handleLogin = async (values, actions) => {
      try {
         setError(null)
         const response = await apiFetch('/auth/session', {
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
            body: JSON.stringify(values)
         })

         if (response.ok) {
            window.location.assign(redirect || '/')
         } else {
            const result = await response.json()
            setError(result?.message || 'An unexpected error occurred')
            actions.setSubmitting(false)
         }
      } catch (err) {
         setError(err.message)
         actions.setSubmitting(false)
      }
   }

   return (
      <AccountContainer logoutActionMessage="login as someone else">
         <Helmet title={`Login | ${organization.name}`} />
         <Formik
            onSubmit={handleLogin}
            render={({ handleSubmit, isSubmitting }) => (
               <Form
                  elementAppearance="plain"
                  elementBackgroundColor="#eee"
                  elementFocusBackgroundColor="#f3f3f3"
                  onSubmit={handleSubmit}>
                  <Text
                     size="9[a-d] 10[e-f]"
                     letterSpacing="-1px"
                     weight="medium"
                     color="#000">
                     Login to the Community
                  </Text>
                  <View float="left" width="100%" height="12px" />
                  <Text size="3[a-d] 3[e-f]" color="#666">
                     {`Welcome to the ${organization.name} Community! After you log in, you'll be able to explore our ${arrayToSentenceFragment(enabledInitiativeTypeNames).trim()}.`}
                  </Text>
                  <View float="left" width="100%" height="20px" />
                  {error && (
                     <NotificationInline
                        borderRadius="3px"
                        type="error"
                        title="Oops!"
                        message={error}
                     />
                  )}
                  <View
                     float="left"
                     width="100%"
                     marginTop="10px"
                     marginBottom="10px">
                     <Grid columns="1" spacing="10px">
                        <GridCell>
                           <Input
                              type="text"
                              name="email"
                              placeholder="Email"
                              required
                              size="lg"
                           />
                        </GridCell>
                        <GridCell>
                           <Input
                              type="password"
                              name="password"
                              placeholder="Password"
                              required
                              size="lg"
                           />
                        </GridCell>
                     </Grid>
                  </View>
                  <View
                     float="left"
                     width="100%"
                     height={organization.privacy === 'open'
                        ? '120px[a-b] 60px[c-f]'
                        : '78px[a-b] 60px[c-f]'
                     }
                     display="flex"
                     flexFlow="column[a-b] row[c-f]"
                     justifyContent="space-between"
                     alignItems="center">
                     <Button
                        color={highlightColor}
                        type="submit"
                        mode={isSubmitting ? 'loading' : 'normal'}
                        name="Login"
                        width="100%[a-b] auto[c-f]"
                        size="lg"
                     />
                     {organization.privacy === 'open' && (
                        <Link to="/account/register">
                           <Text size="2" weight="medium" color="#888">Create Account</Text>
                        </Link>
                     )}
                     <Link to="/account/forgot-password">
                        <Text size="2" weight="medium" color="#888">Forgot Password?</Text>
                     </Link>
                  </View>
               </Form>
            )}
         />
      </AccountContainer>
   )
}

export default Login
